
import { defineComponent, PropType } from '@vue/runtime-core';
import { ClientConstants, Duration } from './../client.constants';

export interface Values {
  key: number;
  leftValue: string | Duration;
  rightValue?: string | Duration;
}

export type Options = Array<Values>;

export default defineComponent({
  name: 'SliderComponent',
  props: {
    id: { type: String, required: true },
    min: { type: Number, default: 0, required: false },
    max: { type: Number, default: 5, required: false },
    step: { type: Number, default: 1, required: false },
    value: { type: Number, default: 0, required: false },
    values: {
      type: Array as PropType<Options>,
      required: true,
      validator(obj: { key: number; leftValue: string | Duration; rightValue: string | Duration }) {
        return obj.key !== null && obj.leftValue !== null && obj.rightValue !== null;
      }
    }
  },
  data: () => {
    return {
      clientConstants: ClientConstants
    };
  },
  computed: {
    /*
     * checks for the value in the values array passed in from the parent
     * returns the value if found, empty string if not
     */
    selectedValue(): string[] {
      const pair = this.values.find(obj => obj.key === this.value);
      if (pair) {
        return pair.rightValue ? [pair.leftValue, pair.rightValue] : [pair.leftValue, ''];
      }
      return ['', ''];
    },
    sliderLabel(): string {
      return `${this.id}-label`;
    },
    /*
     * uses the slider value to determine the percentage of the slider bar to
     * be filled, then uses this to create a gradient background.
     */
    sliderBackground(): { background: string } {
      const value = ((this.value - this.min) / (this.max - this.min)) * 100;
      return {
        background:
          'linear-gradient(to right, #0D6F3F 0%, #0D6F3F ' + value + '%, #3687274F ' + value + '%, #3687274F 100%)'
      };
    },
    /*
     * gives the string to be read for accessibility voiceover
     */
    screenReaderText(): string {
      return `Value selected: ${this.selectedValue[0]}, ${this.selectedValue[1]}`;
    }
  },
  methods: {
    /*
     * when input is changed, this function is called so the parent component
     * can handle the emitted value
     */
    displayValue(event: Event): void {
      this.$emit('sliderValueChanged', (event.target as HTMLInputElement).value);
    },
    validateGreaterThanZero(value: number): boolean {
      if (value > 0) {
        return true;
      }
      console.error(this.clientConstants.INVALID_SLIDER_GREATER_THAN_ZERO_MSG);
      return false;
    },
    validateGreaterThanOrEqualZero(value: number): boolean {
      if (value >= 0) {
        return true;
      }
      console.error(this.clientConstants.INVALID_SLIDER_GREATER_THAN_OR_EQUAL_ZERO_MSG);
      return false;
    },
    validateProps(): void {
      this.validateGreaterThanOrEqualZero(this.min);
      this.validateGreaterThanZero(this.max);
      this.validateGreaterThanZero(this.step);
      this.validateGreaterThanOrEqualZero(this.value);

      if (this.min >= this.max) {
        console.error(this.clientConstants.INVALID_SLIDER_MIN_VALUE_MSG);
        return;
      }
      if (this.step > this.max || this.step < 1) {
        console.error(this.clientConstants.INVALID_SLIDER_STEP_VALUE_MSG);
        return;
      }
      if (this.value > this.max || this.value < this.min) {
        console.error(this.clientConstants.INVALID_SLIDER_VALUE_MSG);
      }
    }
  },
  watch: {
    $props: {
      immediate: true,
      handler() {
        this.validateProps();
      }
    }
  }
});
